// src/components/customers/CustomersFilter.jsx
import React from "react";
import { useCustomerGroupsQuery, useCustomersQuery } from "@queries/customers/index";

// Custom hook for debouncing search input
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const initial = { customer_id:"", name:"", email:"", telephone:"", customer_group_id:"" };

export default function CustomersFilter({ value, onChange }) {
  const [form, setForm] = React.useState(value || initial);
  const [showDropdown, setShowDropdown] = React.useState(false);

  React.useEffect(()=>setForm(value || initial), [value]);

  const { data: groupsData } = useCustomerGroupsQuery();
  const groups = groupsData?.data || [];

  // Search autocomplete query
  const debouncedName = useDebounce(form.name, 300);
  const { data: searchResults } = useCustomersQuery(
    { name: debouncedName },
    1,
    5
  );
  
  const suggestions = (debouncedName && debouncedName.length >= 2) ? (searchResults?.data || []) : [];

  const setField = (k,v)=>setForm((f)=>({ ...f, [k]: v }));
  const submit = (e) => { 
    e?.preventDefault?.(); 
    setShowDropdown(false);
    
    // Trim leading zero from telephone if it exists
    const submittedForm = { ...form };
    if (submittedForm.telephone && submittedForm.telephone.startsWith('0')) {
      submittedForm.telephone = submittedForm.telephone.replace(/^0+/, '');
    }
    
    onChange?.(submittedForm); 
  };
  const reset = ()=>{ setForm(initial); setShowDropdown(false); onChange?.(initial); };

  const handleSelectSuggestion = (customer) => {
    const updatedForm = { ...form, name: customer.name };
    setForm(updatedForm);
    setShowDropdown(false);
    
    // Auto submit upon selection
    const submittedForm = { ...updatedForm };
    if (submittedForm.telephone && submittedForm.telephone.startsWith('0')) {
      submittedForm.telephone = submittedForm.telephone.replace(/^0+/, '');
    }
    onChange?.(submittedForm);
  };

  return (
    <form onSubmit={submit}>
      <div className="oc-f-grid">
      <div className="oc-field">
        <label>Customer ID</label>
        <input className="oc-input" value={form.customer_id} onChange={(e)=>setField("customer_id", e.target.value)} placeholder="Customer ID" />
      </div>
      <div className="oc-field large" style={{ position: "relative" }}>
        <label>Customer Name</label>
        <input 
          className="oc-input" 
          value={form.name} 
          onChange={(e)=>{
            setField("name", e.target.value);
            setShowDropdown(true);
          }} 
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          placeholder="Customer Name" 
          autoComplete="off"
        />
        {showDropdown && suggestions.length > 0 && (
          <ul style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1000,
            listStyle: "none",
            padding: 0,
            margin: 0,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            {suggestions.map(c => (
              <li 
                key={c.id || c.customer_id} 
                onClick={() => handleSelectSuggestion(c)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #eee",
                  display: "flex",
                  flexDirection: "column"
                }}
                onMouseDown={(e) => e.preventDefault()} // prevent blur from closing before click
              >
                <strong>{c.name}</strong>
                {c.telephone && <span style={{ fontSize: "12px", color: "#666" }}>{c.telephone}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="oc-field large">
        <label>E-Mail</label>
        <input className="oc-input" value={form.email} onChange={(e)=>setField("email", e.target.value)} placeholder="E-Mail" />
      </div>
      <div className="oc-field large">
        <label>Telephone</label>
        <input className="oc-input" value={form.telephone} onChange={(e)=>setField("telephone", e.target.value)} placeholder="Telephone Number" />
      </div>
      <div className="oc-field large">
        <label>Customer Group</label>
        <select className="oc-select" value={form.customer_group_id} onChange={(e)=>setField("customer_group_id", e.target.value)}>
          <option value="">All</option>
          {groups.map(g=>(
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      </div>
      <div className="oc-actions" style={{ marginTop:8 }}>
        <button className="oc-btn" type="submit">Filter</button>
        <button className="oc-btn ghost" type="button" onClick={reset}>Reset</button>
      </div>
    </div>
    </form>
  );
}

